import React, { useContext } from 'react';
import { FieldTemplateProps } from '@rjsf/utils';
import { RowLayoutContext } from './rowLayout';
import RequiredTag from 'shared/components/forms/RequiredTag';

const HorizontalFieldTemplate = ({
  id,
  label,
  help,
  required,
  description,
  errors,
  children,
  displayLabel,
  classNames,
  disabled,
  readonly,
  schema,
  uiSchema,
  onKeyChange,
  onDropPropertyClick,
}: FieldTemplateProps) => {
  const rowRole = useContext(RowLayoutContext);
  // The divider under each field is dropped on the last VISIBLE one, not on
  // `:last-child`: connectors list hidden fields (run_on_schedule) at the end
  // of ui:order, and their bare <input type="hidden"> is what sits last, so a
  // `last:` rule never fired and the form ended on a stray line.
  // Don't render anything for hidden fields
  if (uiSchema?.['ui:widget'] === 'hidden') {
    return <>{children}</>;
  }

  // Row-layout arrays (see rowLayout.ts). The item object carries no label of
  // its own; a property of it is a cell holding only its control, since the
  // list's caption line names it. Below `sm` the caption line is hidden and
  // cells stack, so each cell shows its label there instead.
  if (rowRole === 'item') {
    return (
      <div className={classNames}>
        {children}
        {errors}
        {help}
      </div>
    );
  }
  if (rowRole === 'cell') {
    return (
      <div className={`${classNames} w-full min-w-0`}>
        {/* rjsf turns displayLabel off for booleans, whose switch draws no
            label of its own, so they need theirs here too. */}
        {(displayLabel || schema.type === 'boolean') && label && (
          <div className="px-2 sm:hidden">
            {label}
            {required && <RequiredTag size="sm" />}
          </div>
        )}
        <RowLayoutContext.Provider value={null}>
          <div className="p-2">{children}</div>
        </RowLayoutContext.Provider>
        {errors}
        {help}
      </div>
    );
  }

  // Full-width fields: the widget spans the whole form row instead of the default
  // 50/50 label/field split. Used by space-hungry widgets (e.g. the collar-
  // registration file table). Flagged via `ui:options.fullWidth` in generateUISchema.
  // The widget owns its own header (label + description + actions) so it can lay them
  // out across the full width — see CustomFileField.
  // WARNING (footgun): in this branch the template renders ONLY the widget — it does
  // NOT render the field label/description. Only flag a field fullWidth if its widget
  // draws its own header; otherwise the field's label silently disappears.
  // NOTE: this fullWidth mechanism will likely be refactored once Vectronic-style
  // integrations are managed through "Source Configurations" rather than through
  // data-provider configurations.
  if ((uiSchema?.['ui:options'] as any)?.fullWidth === true) {
    return (
      <div
        className={`${classNames} mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300 [&:not(:has(~div))]:mb-0 [&:not(:has(~div))]:border-b-0 [&:not(:has(~div))]:pb-0`}
      >
        <div className="w-full">{children}</div>
        {errors}
        {help}
      </div>
    );
  }

  // Boolean fields use the same two-column layout as text fields.
  // Note: rjsf sets displayLabel=false for booleans by default (expecting the widget to render it),
  // so we render label unconditionally here instead of using displayLabel.
  if (schema.type === 'boolean') {
    return (
      <div
        className={`${classNames} mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300 [&:not(:has(~div))]:mb-0 [&:not(:has(~div))]:border-b-0 [&:not(:has(~div))]:pb-0`}
      >
        <div className="flex w-full flex-row items-stretch justify-between gap-6">
          <div className="flex w-1/2 min-w-0 flex-col">
            <span className="mr-1">{label}</span>
            {description}
          </div>
          <div className="flex w-1/2 min-w-0 items-center justify-end p-2">
            {children}
          </div>
        </div>
        {errors}
        {help}
      </div>
    );
  }

  // For nested objects/arrays, use vertical layout. A list takes the same
  // divider as the fields around it, so a list followed by a field is
  // separated like any other pair. Objects stay bare: the form's root is one.
  // A reference list renders as one multi-select box (see applyReferenceWidgets),
  // so it sits beside its label like any other field.
  const isReferenceList =
    schema.type === 'array' && !!uiSchema?.['gundi:reference'];
  if (
    (schema.type === 'object' || schema.type === 'array') &&
    !isReferenceList
  ) {
    const divider =
      schema.type === 'array'
        ? ' mb-[8px] border-b border-ui-divider-lines [&:not(:has(~div))]:border-b-0 pb-[8px] [&:not(:has(~div))]:pb-0 [&:not(:has(~div))]:mb-0'
        : '';
    return (
      <div className={`${classNames}${divider}`}>
        {displayLabel && label && <div>{label}</div>}
        <div>{children}</div>
        {errors}
        {help}
      </div>
    );
  }

  return (
    <div
      className={`${classNames} mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300 [&:not(:has(~div))]:mb-0 [&:not(:has(~div))]:border-b-0 [&:not(:has(~div))]:pb-0`}
    >
      {/* Squeeze: keep the label and field columns side-by-side at every width and
                let them shrink together. `min-w-0` defeats flexbox's default min-width:auto
                so the full-width input compresses instead of overflowing a narrow card.
                NOTE: responsive breakpoints across these forms are viewport-based. The real
                constraint is the card/column width — if a form is ever rendered in a narrow
                column (e.g. a side panel), switch these toggles to CSS container queries. */}
      {/* The field is centred on its label and description, like every
                other control in these forms (list Add buttons, switches). */}
      {/* A reference list's pills need the whole row on a phone, so below
                `sm` the box moves under its label and description. */}
      <div
        className={`flex w-full justify-between ${isReferenceList ? 'flex-col gap-1 sm:flex-row sm:items-center sm:gap-6' : 'flex-row items-center gap-6'}`}
      >
        {/* A list item's label ("Project IDs-1") is right-aligned so it
                    sits next to its own input instead of across the gap. rjsf ids
                    a list item by its index, so the id ends in _<n>. */}
        <div
          className={`flex min-w-0 flex-col ${isReferenceList ? 'w-full sm:w-1/2' : 'w-1/2'} ${/_\d+$/.test(id) ? 'items-end text-right' : ''}`}
        >
          <span className="mr-1">
            {displayLabel && label}
            {/* A top-level field's label is generateUISchema's title, which
                carries its own marker; a field inside a list row has a plain
                string label and needs one here. A list item ("Tags-1") is
                not a field: rjsf calls every non-null item required. */}
            {displayLabel &&
              label &&
              required &&
              typeof label === 'string' &&
              !/_\d+$/.test(id) && <RequiredTag size="sm" />}
          </span>
          {description}
        </div>
        <div
          className={`min-w-0 ${isReferenceList ? 'w-full sm:w-1/2' : 'w-1/2'}`}
        >
          <div className="p-2">{children}</div>
          {errors}
          {help}
        </div>
      </div>
    </div>
  );
};

export default HorizontalFieldTemplate;
